/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */

import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { IdleTimerProvider } from 'react-idle-timer';

import { ADMIN_ENFORCE_PASSWORD, ADMIN_JWT_TOKEN, APPLICATION_TOKEN_CHECK_TIMER, CLIENT_ENFORCE_PASSWORD, LAST_API_TIME, SESSION_CHECK_JWT_TOKEN_THRESHOLD, SESSION_CHECK_JWT_TOKEN_THRESHOLD_FOR_UPDATE } from '../constants/appConfig';
import history from '../utils/history';
import { getLocalStorage, setLocalStorage } from '../utils/storageUtil';
import {
  isAdminAuthenticated,
  isClientAuthenticated,
  isSystemAuthenticated,
} from '../utils/jwtUtil';
import { Redirect } from 'react-router-dom/cjs/react-router-dom.min';
import { fetchAdminAPI } from '../utils/httpUtil';

export const PrivateAdminRoute = ({
  component: Component,
  layout: Layout,
  RedirectUrl: redirectUrl,
  ...rest
}) => {
  const [timeoutTimer, setTimeoutTimer] = useState(
    APPLICATION_TOKEN_CHECK_TIMER
  );
  const [timerUpdateLoading, setTimerUpdateLoading] = useState(false);

  const onIdle = () => {
    setTimeoutTimer(getTokenRemainingTimeToExpire());
    if (getTokenRemainingTimeToExpire() < SESSION_CHECK_JWT_TOKEN_THRESHOLD) {
      if (!isAdminAuthenticated()) {
        history?.push(redirectUrl);
      }
    }
  };

  const onAction = () => {
    if (getLocalStorage(LAST_API_TIME) < Date.now()) {
      if (!isAdminAuthenticated()) {
        history?.push(redirectUrl);
      } else {
        setTimerUpdateLoading(true);
        if (!timerUpdateLoading) {
          fetchAdminAPI('atom-manager/v1/notifications/count')
            .finally(() => {
              setTimerUpdateLoading(false);
            });
        }
      }
    }
  };

  useEffect(() => {
    setLocalStorage(LAST_API_TIME, Date.now() + 600000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLocalStorage(ADMIN_JWT_TOKEN)]);

  return (
    <Route
      {...rest}
      render={props => {
        if (!isAdminAuthenticated()) {
          // not logged in so redirect to login page with the return url
          return (
            <Redirect
              to={{
                pathname: redirectUrl,
                state: { from: props.location },
              }}
            />
          );
        } else if (getLocalStorage(ADMIN_ENFORCE_PASSWORD)) {
          history.push('/admin/enforcepassword');
        } else {
          // authorised so return component
          return (
            <IdleTimerProvider
              timeout={
                1000 *
                (timeoutTimer - SESSION_CHECK_JWT_TOKEN_THRESHOLD_FOR_UPDATE)
              }
              onIdle={onIdle}
              onAction={onAction}
            >
              <Layout>
                <Component {...props} />
              </Layout>
            </IdleTimerProvider>
          );
        }
      }}
    />
  );
}

export const PrivateClientRoute = ({
  component: Component,
  layout: Layout,
  RedirectUrl: redirectUrl,
  ...rest
}) => (
  <Route
    {...rest}
    render={props => {
      if (!isClientAuthenticated()) {
        // not logged in so redirect to login page with the return url
        return (
          <Redirect
            to={{
              pathname: redirectUrl,
              state: { from: props.location },
            }}
          />
        );
      } else if (getLocalStorage(CLIENT_ENFORCE_PASSWORD)) {
        history.push('/client/enforcepassword');
      } else {
        // authorised so return component
        return (
          <Layout>
            <Component {...props} />
          </Layout>
        );
      }
    }}
  />
);

export const PrivateSystemRoute = ({
  component: Component,
  layout: Layout,
  RedirectUrl: redirectUrl,
  ...rest
}) => (
  <Route
    {...rest}
    render={props => {
      if (!isSystemAuthenticated()) {
        // not logged in so redirect to login page with the return url
        return (
          <Redirect
            to={{
              pathname: redirectUrl,
              state: { from: props.location },
            }}
          />
        );
      }

      // authorised so return component
      return (
        <Layout>
          <Component {...props} />
        </Layout>
      );
    }}
  />
);
